import { makeValuesPredicate } from '@sgme/fp';
import type { PlacedOrderResponse } from 'apiConnector/secondary-market/cp-order/model';
import { cpOrderAmendActions } from 'state/secondaryMarket/cpOrder/amend/amendedOrder.actions';
import { fromSecondaryMarket, fromSecondaryPostEventsWorkspace } from 'state/selectors';
import type { Thunk } from 'state/state.common';
import { workspaceTabFrozen, workspaceTabSetBusy, workspaceTabUnfrozen } from 'state/workspaces/workspace.actions';

import type { HttpError } from '../../../../utils/http/httpClient.model';
import { extractHttpErrorMessage } from '../../../../utils/http/httpClient.model';
import { secondaryMarketCpOrderFormReset } from '../cpOrderForm';
import { secondaryMarketAmendCpOrderFetchRequested } from '../cpOrderForm/original/cpOrderOriginalForm.actions';
import { getOriginalFormETag } from '../cpOrderForm/original/cpOrderOriginalForm.selector';

export function secondaryMarketCpOrderAmendRequestedThunk(tabId: string): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    const etag = getOriginalFormETag(state, tabId);
    const request = fromSecondaryMarket.cpOrder.getAmendOrderRequest(state, tabId);
    const orderId = fromSecondaryPostEventsWorkspace.getOrderId(state, tabId);
    if (etag !== undefined) {
      dispatch(cpOrderAmendActions.secondaryMarketAmendRequested(tabId, orderId, etag, request));
      dispatch(workspaceTabFrozen(tabId, 'secondaryMarketPostEvents', true));
    }
  };
}

export function secondaryMarketCpOrderAmendResetTabThunk(tabId: string): Thunk<void> {
  return (dispatch, getState) => {
    const state = getState();
    const orderId = fromSecondaryPostEventsWorkspace.getOrderId(state, tabId);
    if (orderId === undefined) {
      return;
    }
    dispatch(workspaceTabUnfrozen(tabId, 'secondaryMarketPostEvents', false));
    dispatch(secondaryMarketCpOrderFormReset(tabId));
    dispatch(secondaryMarketAmendCpOrderFetchRequested(tabId, orderId));
  };
}

const isServerError = makeValuesPredicate([500, 502, 504]);

export function secondaryMarketCpOrderAmendFailedThunk(tabId: string, error?: HttpError): Thunk<void> {
  return (dispatch, _) => {
    const errorStatus = error?.status ?? 500;

    const message = extractHttpErrorMessage(error);

    if (isServerError(errorStatus)) {
      dispatch(cpOrderAmendActions.secondaryMarketAmendFailed(tabId, message));
    } else if (errorStatus === 412) {
      dispatch(cpOrderAmendActions.secondaryMarketAmendCollided(tabId, message));
    } else {
      dispatch(cpOrderAmendActions.secondaryMarketAmendRejected(tabId, message));
    }
    dispatch(workspaceTabSetBusy(tabId, 'secondaryMarketPostEvents', false));
  };
}

export function secondaryMarketCpOrderAmendReceivedThunk(tabId: string, data: PlacedOrderResponse): Thunk<void> {
  return (dispatch, _) => {
    dispatch(cpOrderAmendActions.secondaryMarketAmendReceived(tabId, data));
    dispatch(workspaceTabSetBusy(tabId, 'secondaryMarketPostEvents', false));
  };
}
